import { createContext, useContext, useReducer, ReactNode, Dispatch, useMemo } from "react";

// Types
export interface AddressFormData {
  account_details: {
    is_active: boolean;
    account_name: string;
    account_number: string;
    account_type: "company" | "general";
    label_format: "pdf" | "zpl";
    available_to_locations: string[];
    fedex_end_user_license_agreement: boolean;
    inv_reference_field: string | null;
    ref_reference_field: string | null;
    po_reference_field: string | null;
    dept_reference_field: string | null;
  };
  address_line_1: string;
  address_line_2: string;
  city: string;
  postal_code: string;
  state_code?: string;
  country_code: string;
}

export interface AddressValidationResponse {
  phone_number: string;
  email: string;
  mfa_required: boolean;
  mfa_options: {
    invoice: string;
    secureCode: string[];
  };
}

export interface CreateParcelAccountState {
  addressFormData: AddressFormData | null;
  addressValidationResponse: AddressValidationResponse | null;
}

// Actions
type Action = {
  type: "SET_VALIDATION_SUCCESS";
  payload: {
    addressFormData: AddressFormData;
    addressValidationResponse: AddressValidationResponse;
  };
};

// Initial State
export const initialState: CreateParcelAccountState = {
  addressFormData: null,
  addressValidationResponse: null,
};

// Reducer
export function reducer(state: CreateParcelAccountState, action: Action): CreateParcelAccountState {
  switch (action.type) {
    case "SET_VALIDATION_SUCCESS":
      return {
        ...state,
        addressFormData: action.payload.addressFormData,
        addressValidationResponse: action.payload.addressValidationResponse,
      };
    default:
      return state;
  }
}

// Context
interface CreateParcelAccountContextType {
  state: CreateParcelAccountState;
  dispatch: Dispatch<Action>;
}

const CreateParcelAccountContext = createContext<CreateParcelAccountContextType | undefined>(undefined);

// Provider
interface CreateParcelAccountProviderProps {
  children: ReactNode;
}

export function CreateParcelAccountProvider({ children }: CreateParcelAccountProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const contextValue = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return <CreateParcelAccountContext.Provider value={contextValue}>{children}</CreateParcelAccountContext.Provider>;
}

// Custom hook
export function useCreateParcelAccountContext() {
  const context = useContext(CreateParcelAccountContext);
  if (context === undefined) {
    throw new Error("useCreateParcelAccountContext must be used within a CreateParcelAccountProvider");
  }
  return context;
}
